import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type StackedBarsView = {
  /** Figures above the plot; none draws no KPI row. */
  kpis: KPIMetric[];
  /** One row per bar, one key per segment. */
  rows: ChartRow[];
  xKey: string;
  /** The segments, each in its own entity's colour, `Other` last. */
  series: ChartSeries[];
  formatValue: (value: number) => string;
};

export type StackedBarsCardProps = WidgetBlockProps & {
  view?: StackedBarsView;
  title: string;
  helpText: string;
  helpSupportingText?: string;
  emptyLabel?: string;
  /** Opens the conversations behind a bar (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Horizontal bars, each stacked by what it is made of, in counts — Customer
 * voice's intents by contributing topic (GP, 2026-11-05). Horizontal because
 * the bars are named (intents), and a name reads along its bar.
 */
export function StackedBarsCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  title,
  helpText,
  helpSupportingText,
  emptyLabel,
  onViewDetails,
}: StackedBarsCardProps) {
  const hasData = view !== undefined && view.series.length > 0 && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title={title}
      helpText={helpText}
      helpSupportingText={helpSupportingText}
      emptyLabel={emptyLabel}
      kpi={view && view.kpis.length > 0 ? <KPI metrics={view.kpis} /> : undefined}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            stacked
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
